<script>
import IconBtnComponent from './IconBtn.vue'
import debounce from 'lodash/debounce'
import store from '../store'
import { getDateString, getDiffDays, normalizeDate } from '../date'

export default {
    // state
    data () {
        return {
            currSlideIdx: 0,
            from: null,
            isResizing: false,
            layout: null,
            slides: [],
            store,
            types: {
                available: {
                    key: 'available',
                    display: "Available",
                },
                unavailable: {
                    key: 'unavailable',
                    display: "Unavailable",
                },
                arrivalOnly: {
                    key: 'arrival_only',
                    display: "Arrive Only",
                },
                departureOnly: {
                    key: 'departure_only',
                    display: "Depart Only",
                },
            }
        }
    },
    props: {
        duration: {
            default: 250,
            type: Number,
        },
        isSelectable: Boolean,
        // availability
        availability: Object,
        hasAvailabilityData: Boolean,
        isGettingAvailability: Boolean,
        // dates
        arrival: String, // expected format (Y-m-d)
        departure: String, // expected format (Y-m-d)
        date: Object,
        startDate: Object,
        endDate: Object,
        // rate plans
        ratePlan: String,
        ratePlans: {
            default: {
                points: {
                    key: 'points',
                    display: 'Vacatia Points',
                },
                usd: {
                    key: 'usd',
                    display: 'USD',
                },
            },
            type: Object,
        },
        // slides
        slideCount: {
            default: 1,
            type: Number,
        },
        slideSize: {
            default: 'base',
            type: String,
        },
    },
    computed: {
        arrivalDate () {
            return this.arrival && new Date(this.arrival)
        },
        departureDate () {
            return this.departure && new Date(this.departure)
        },
        stayLength () {
            if (!this.arrival || !this.departure) {
                return 0
            }
            return getDiffDays([ this.departureDate, this.arrivalDate ])
        },
        start () {
            return Math.max(this.currSlideIdx-1,0)
        },
        end () {
            return this.currSlideIdx + this.slideCount + 1
        },
        isPrevDisabled () {
            return this.isGettingAvailability || this.currSlideIdx < 1
        },
        isNextDisabled () {
            return this.isGettingAvailability || this.currSlideIdx + this.slideCount > this.slides.length - 1
        },
        navBtnClass () {
            return {
                'tw-top-1': this.slideCount < 2,
                'tw-top-[161px] tw-m-1': this.slideSize !== 'lg' && this.slideCount > 1,
                'tw-top-[194px] tw-mx-2 tw-w-9 tw-h-9': this.slideSize === 'lg' && this.slideCount > 1,
                'tw-w-4 tw-h-4': this.slideSize !== 'lg',
                'tw-w-6 tw-h-6': this.slideSize === 'lg' && this.slideCount < 2,
            }
        },
        slideGap () {
            return this.slideSize === 'lg' ? 28 : 18
        },
        slideWidth () {
            return this.slideSize === 'lg' ? 338 : 254
        },
        trayHeight () {
            return this.slideSize === 'lg' ? 356 : 282
        },
        trayWidth () {
            if (this.slideCount > 1) return this.slideWidth * 2 + this.slideGap
            return this.slideWidth
        },
    },
    methods: {
        getRate (date) {
            const dateString = getDateString(date)
            return this.isAvailable(date) ?
                this.availability[dateString][this.ratePlan] :
                null
        },
        getFormattedRate (date) {
            let val = this.getRate(date)
            if (val && this.ratePlan === 'usd') {
                val = '$'+Math.round(val)
            }
            return val
        },
        getArrivalOffset (date) {
            if (!this.arrival) {
                return 0
            }
            return getDiffDays([ this.arrivalDate, date ])
        },
        isAvailable (date) {
            if (this.hasAvailabilityData) {
                const dateString =
                    date === null ? null :
                        typeof date === 'string' ? date :
                            getDateString(date)
                const val =
                    this.availability &&
                        this.availability[dateString] &&
                            this.availability[dateString][this.ratePlan]
                return !!val
            }
            return date >= this.startDate && date <= this.endDate
        },
        isArrival (date) {
            if (this.arrival) {
                return date.getTime() === this.arrivalDate.getTime()
            }
            return false
        },
        isDeparture (date) {
            if (this.departure) {
                return date.getTime() === this.departureDate.getTime()
            }
            return false
        },
        isBoundary (date) {
            return this.isArrival(date) || this.isDeparture(date)
        },
        isSelected (date) {
            if (
                this.arrival && date.getTime() >= this.arrivalDate.getTime() &&
                this.departure && date.getTime() <= this.departureDate.getTime()
            ) {
                return true
            }
            return this.isBoundary(date)
        },
        next () {
            this.currSlideIdx += 1
        },
        prev () {
            this.currSlideIdx -= 1
        },
        onResize () {
            this.isResizing = true
            // debounced `isResizing` reset
            this.onResizeDone()
        },
        onSlideCount (newVal, oldVal) {
            // handle layout change (single to double)
            if (newVal > oldVal) {
                // was last position
                if (this.slides.length - this.currSlideIdx === 1) this.prev()
            }
            // handle layout change (double to single)
            if (newVal < oldVal) {
                // was last position
                if (this.slides.length - this.currSlideIdx === 2) this.next()
            }
        },
    },
    watch: {
        'store.windowWidth': 'onResize',
        'slideCount': 'onSlideCount',
    },
    // lifecycle
    created () {
        const that = this
        this.onResizeDone = debounce(() => that.isResizing = false, this.duration + 100)

        // generate slide data
        const monthStrings = [
            'January', 'February', 'March', 'April', 'May', 'June', 'July',
            'August', 'September', 'October', 'November', 'December',
        ]
        const targetDate = new Date(this.endDate.getUTCFullYear(), this.endDate.getUTCMonth())
        const getHeading = (date) => monthStrings[date.getUTCMonth()] + ' ' + date.getUTCFullYear()
        const getMatrix = (date) => {
            const year = date.getFullYear()
            const month = date.getMonth()
            const firstDate = new Date(year, month)
            const offset = firstDate.getDay() - 1
            const rows = []
            let cell = null
            let cellPrev = null
            let rowIndex = null
            let row = null
            for (let i = 0; i < 42; i++) {
                cell = {}
                cell.date = new Date(year, month, i - offset)
                cell.date = normalizeDate(cell.date)
                cell.datePrev = cellPrev ? cellPrev.date : null
                cell.isInScope = cell.date.getUTCMonth() === month
                cell.isPast = cell.date < this.startDate
                cell.isToday = cell.date.getTime() === this.startDate.getTime()
                cell.isTooFarOut = cell.date.getTime() > this.endDate.getTime()
                cell.isSelectable = this.isSelectable && !cell.isPast && !cell.isTooFarOut
                rowIndex = Math.floor(i/7)
                row = rows[rowIndex] = rows[rowIndex] || []
                row.push(cell)
                cellPrev = cell
            }
            return rows
        }
        let date = null
        let i = 0
        while (!date || date.getTime() < targetDate.getTime()) {
            date = new Date(this.startDate.getUTCFullYear(), this.startDate.getUTCMonth() + i)
            this.slides.push({ date, heading: getHeading(date), idx: i++, matrix: getMatrix(date) })
        }

        // set current slide index
        date = this.date || this.startDate
        const y = date.getUTCFullYear()
        const m = date.getUTCMonth()
        const match = (date) => date.getUTCFullYear() === y && date.getUTCMonth() === m
        i = this.slides.findIndex(e => match(e.date))
        if (this.slideCount > 1 && match(new Date(this.departure))) {
            i -= 1
        }
        i = Math.max(i, 0)
        i = Math.min(i, this.slides.length-1)
        this.currSlideIdx = i
    },
    // misc.
    components: {
        IconBtn: IconBtnComponent,
    },
}
</script>

<template>
    <div class="tw-box-border tw-mx-auto tw-w-fit [&_*]:tw-box-border vca-vue-datepicker" v-bind="{ 'data-arrival': arrivalDate?.toUTCString() }">
        <header
            class="tw-flex tw-justify-end tw-mb-6"
            v-if="$slots.header"
            v-bind:style="{
                'width': trayWidth + 'px',
            }"
        >
            <slot name="header"></slot>
        </header>
        <div class="tw-relative">
            <div
                class="
                    tw-absolute
                    tw-inset-0
                    tw-flex
                    tw-flex-col
                    tw-justify-center
                    tw-items-center
                    tw-space-y-4
                    tw-text-center
                    tw-bg-white/75
                "
                role="status"
                v-if="isGettingAvailability"
            >
                <div class="tw-text-2xl">Searching for availability!</div>
                <div aria-hidden="true" class="tw-loader"></div>
            </div>
            <div
                class="
                    tw-absolute
                    tw-overflow-hidden
                    tw-relative
                    tw-m-0
                "
                v-bind:class="{ 'tw-z-[-1]': isGettingAvailability }"
                v-bind:style="{
                    'height': trayHeight + 'px',
                    'width': trayWidth + 'px',
                }"
            >
                <div
                    class="tw-absolute"
                    v-for="slide in slides.slice(start,end)"
                    v-bind="{
                        'aria-hidden': slide.idx >= currSlideIdx && slide.idx < currSlideIdx + slideCount ? null : true,
                        'data-index': slide.idx,
                    }"
                    v-bind:key="slide.idx"
                    v-bind:style="{
                        'left': (slideGap + slideWidth) * (slide.idx - currSlideIdx) + 'px',
                        'width': slideWidth + 'px',
                        'opacity': slide.idx >= currSlideIdx && slide.idx < currSlideIdx + slideCount ? 1 : 0,
                        'transition-duration': isResizing ? 0 : duration + 'ms',
                        'transition-property': isResizing ? 'none' : 'left, opacity',
                    }"
                >
                    <h1
                        class="tw-m-0 tw-font-normal"
                        v-text="slide.heading"
                        v-bind:class="{
                            'tw-mb-2': slideSize === 'lg',
                            'tw-mb-3.5': slideSize !== 'lg',
                            'tw-text-base': slideSize !== 'lg',
                            'tw-text-2xl': slideSize === 'lg',
                            'tw-text-center': slideCount < 2,
                        }"
                    ></h1>
                    <table class="tw-border-collapse tw-text-center">
                        <thead>
                            <tr>
                                <th
                                    class="
                                        tw-p-0
                                        tw-text-[10px]
                                        tw-font-medium
                                        tw-text-white
                                        tw-uppercase
                                        tw-bg-[#acafb0]
                                        tw-border-2
                                        tw-border-solid
                                        tw-border-[#e0e1e2]
                                    "
                                    v-for="string in ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat']"
                                    v-bind:class="{
                                        'tw-w-9 tw-h-4': slideSize !== 'lg',
                                        'tw-w-12 tw-h-6': slideSize === 'lg',
                                    }"
                                >{{ string }}</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="row in slide.matrix">
                                <td
                                    class="vca-vue-datepicker-cell"
                                    v-for="cell in row"
                                    v-bind:class="{
                                        'vca-vue-datepicker-cell--available': cell.isInScope && isAvailable(cell.date),
                                        'vca-vue-datepicker-cell--unavailable': cell.isInScope && !isAvailable(cell.date),
                                        'vca-vue-datepicker-cell--arrival_only': cell.isInScope && isAvailable(cell.date) && !isAvailable(cell.datePrev),
                                        'vca-vue-datepicker-cell--departure_only': cell.isInScope && !isAvailable(cell.date) && isAvailable(cell.datePrev),
                                        'vca-vue-datepicker-cell--selectable': cell.isInScope && isSelectable && (isAvailable(cell.date) || isAvailable(cell.datePrev)),
                                        'vca-vue-datepicker-cell--selected': cell.isInScope && isSelected(cell.date),
                                        'vca-vue-datepicker-cell--boundary': cell.isInScope && isBoundary(cell.date),
                                        'tw-w-9 tw-h-9': slideSize !== 'lg',
                                        'tw-w-12 tw-h-12': slideSize === 'lg',
                                    }"
                                >
                                    <div
                                        class="vca-vue-datepicker-cell-selection"
                                        aria-hidden="true"
                                        v-if="cell.isInScope && !cell.isPast && !cell.isTooFarOut"
                                    >
                                        <div
                                            class="vca-vue-datepicker-cell-selection-bg"
                                            v-bind:style="{
                                                'width': 100 * (stayLength && stayLength + 1) + '%',
                                                'left': 100 * getArrivalOffset(cell.date) + '%',
                                                'transition-duration': duration + 'ms',
                                            }"
                                        >
                                        </div>
                                    </div>
                                    <div
                                        class="vca-vue-datepicker-cell-content"
                                        v-if="cell.isInScope"
                                        v-bind:class="{
                                            'vca-vue-datepicker-cell-content--boundary': isBoundary(cell.date),
                                            'vca-vue-datepicker-cell-content--selectable': isSelectable && (isAvailable(cell.date) || isAvailable(cell.datePrev)),
                                        }"
                                    >
                                        <strong
                                            class="tw-font-normal tw-leading-none"
                                            v-bind:class="{
                                                'tw-text-sm': slideSize !== 'lg',
                                                'tw-text-lg': slideSize === 'lg',
                                                'tw-text-[#2a82e9]': cell.isToday && !isSelected(cell.date),
                                                'tw-text-[#878787]': cell.isPast || cell.isTooFarOut,
                                                'tw-text-white': isBoundary(cell.date),
                                            }"
                                        >{{ cell.date.getUTCDate() }}</strong>
                                        <span
                                            class="
                                                tw-text-[10px]
                                                tw-font-bold
                                                tw-leading-[15px]
                                            "
                                            v-text="getFormattedRate(cell.date)"
                                            v-if="hasAvailabilityData && isAvailable(cell.date)"
                                        ></span>
                                        <button
                                            class="
                                                tw-block
                                                tw-absolute
                                                tw-z-10
                                                tw-inset-0
                                                tw-indent-[-9999em]
                                                tw-bg-[rgba(255,0,0,0)]
                                                tw-border-none
                                            "
                                            type="button"
                                            v-if="isSelectable && (isAvailable(cell.date) || isAvailable(cell.datePrev))"
                                            v-on:click="$emit('toggleDate', { detail: { date: cell.date } })"
                                        >toggle date</button>
                                    </div>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>
            <IconBtn
                class="tw-absolute tw-rotate-90"
                v-on:click="prev"
                v-bind="{
                    disabled: isPrevDisabled,
                    icon: 'arrow',
                    title: 'Load Previous Month',
                }"
                v-bind:class="{
                    ...navBtnClass,
                    'tw-left-0': slideCount < 2,
                    'tw-right-full': slideCount > 1,
                }"
            />
            <IconBtn
                class="tw-absolute tw--rotate-90"
                v-on:click="next"
                v-bind="{
                    disabled: isNextDisabled,
                    icon: 'arrow',
                    title: 'Load Next Month',
                }"
                v-bind:class="{
                    ...navBtnClass,
                    'tw-right-0': slideCount < 2,
                    'tw-left-full': slideCount > 1,
                }"
            />
        </div>
        <footer
            class="tw-mt-6"
            v-if="ratePlan || $slots.actions"
            v-bind:class="{
                'tw-space-y-6': this.slideCount < 2,
                'tw-flex tw-space-x-3': this.slideCount > 1,
                'tw-justify-end': this.slideCount > 1 && !ratePlan,
                'tw-justify-between': this.slideCount > 1 && ratePlan,
            }"
            v-bind:style="{
                'width': trayWidth + 'px',
            }"
        >
            <div v-if="ratePlan">
                <div
                    class="vca-vue-datepicker-key"
                    v-bind:class="{ 'vca-vue-datepicker-key--compact': slideCount < 2, }"
                >
                    <ul
                        class="vca-vue-datepicker-key-items"
                        v-bind:class="{ 'vca-vue-datepicker-key-items--compact': slideCount < 2, }"
                    >
                        <li
                            class="vca-vue-datepicker-key-item"
                            v-for="({ display, key }) in types"
                            v-bind:class="{
                                'vca-vue-datepicker-key-item--compact': slideCount < 2,
                                'vca-vue-datepicker-key-item--available': key === types.available.key,
                                'vca-vue-datepicker-key-item--unavailable': key === types.unavailable.key,
                                'vca-vue-datepicker-key-item--arrival_only': key === types.arrivalOnly.key,
                                'vca-vue-datepicker-key-item--departure_only': key === types.departureOnly.key,
                            }"
                        >{{ display }}</li>
                    </ul>
                </div>
                <p
                    class="
                        tw-m-0
                        tw-mt-4
                        tw-text-xxs
                        tw-font-bold
                        tw-leading-none
                    "
                    v-bind:class="{ 'tw-text-center': slideCount < 2 }"
                >Price shown in {{ ratePlans[ratePlan].display }}</p>
            </div>
            <div
                class="tw-flex"
                v-if="$slots.actions"
                v-bind:class="{
                    'tw-flex-col tw-space-y-3': this.slideCount < 2,
                    'tw-space-x-3': this.slideCount > 1,
                }"
            >
                <slot name="actions"></slot>
            </div>
        </footer>
    </div>
</template>

<style>
.vca-vue-datepicker {
    --vca-vue-datepicker-available-bg: #f4f4f4;
    --vca-vue-datepicker-unavailable-bg: #c7c8c9;
    --vca-vue-datepicker-arrival_only-bg: linear-gradient(
        45deg,
        var(--vca-vue-datepicker-unavailable-bg) 0%,
        var(--vca-vue-datepicker-unavailable-bg) 50%,
        var(--vca-vue-datepicker-available-bg) 50%,
        var(--vca-vue-datepicker-available-bg) 100%
    );
    --vca-vue-datepicker-departure_only-bg: linear-gradient(
        45deg,
        var(--vca-vue-datepicker-available-bg) 0%,
        var(--vca-vue-datepicker-available-bg) 50%,
        var(--vca-vue-datepicker-unavailable-bg) 50%,
        var(--vca-vue-datepicker-unavailable-bg) 100%
    );
    --vca-vue-datepicker-selected-bg: #c0defa;
    --vca-vue-datepicker-boundary-bg: #2a82e9;
}
.vca-vue-datepicker-cell {
    @apply
        tw-relative
        tw-p-0
        tw-bg-[#e5e5e5]
        tw-border-2
        tw-border-solid
        tw-border-[#e0e1e2]
    ;
}
.vca-vue-datepicker-cell--available {
    background: var(--vca-vue-datepicker-available-bg);
}
.vca-vue-datepicker-cell--unavailable {
    background: var(--vca-vue-datepicker-unavailable-bg);
}
.vca-vue-datepicker-cell--arrival_only {
    background: var(--vca-vue-datepicker-arrival_only-bg);
}
.vca-vue-datepicker-cell--departure_only {
    background: var(--vca-vue-datepicker-departure_only-bg);
}
.vca-vue-datepicker-cell-content {
    @apply
        tw-relative
        tw-flex
        tw-flex-col
        tw-justify-center
        tw-w-full
        tw-h-full
    ;
}
.vca-vue-datepicker-cell-content--boundary,
.vca-vue-datepicker-cell-content--selectable:hover {
    background: var(--vca-vue-datepicker-boundary-bg)
}
.vca-vue-datepicker-cell-content--boundary *,
.vca-vue-datepicker-cell-content--selectable:hover * {
    color: white;
}
.vca-vue-datepicker-cell-selection {
    @apply
        tw-absolute
        tw-overflow-hidden
        tw-w-full
        tw-h-full
    ;
}
.vca-vue-datepicker-cell-selection-bg {
    @apply
        tw-absolute
        tw-h-full
        tw-transition-all
    ;
    background: var(--vca-vue-datepicker-selected-bg);
}
.vca-vue-datepicker-key {
    @apply
        tw-mx-auto
        tw-mb-0
    ;
}
.vca-vue-datepicker-key-item {
    @apply
        tw-flex
        tw-justify-center
        tw-items-center
        tw-m-1.5
        tw-text-xs
        tw-font-medium
        tw-whitespace-nowrap
    ;
}
.vca-vue-datepicker-key-item::before {
    @apply
        tw-block
        tw-mr-1
        tw-w-4
        tw-h-4
    ;
    border: 1px solid #e0e1e2;
    content: '';
}
.vca-vue-datepicker-key-item--available::before {
    background: var(--vca-vue-datepicker-available-bg);
}
.vca-vue-datepicker-key-item--unavailable::before {
    background: var(--vca-vue-datepicker-unavailable-bg);
}
.vca-vue-datepicker-key-item--arrival_only::before {
    background: var(--vca-vue-datepicker-arrival_only-bg);
}
.vca-vue-datepicker-key-item--departure_only::before {
    background: var(--vca-vue-datepicker-departure_only-bg);
}
.vca-vue-datepicker-key-items {
    @apply
        tw-flex
        tw-flex-wrap
        tw-p-0
        tw--m-1.5
        tw-list-none
    ;
}
.vca-vue-datepicker-key-items--compact {
    @apply
        tw-justify-center
        tw-mx-auto
    ;
    max-width: 254px;
}
</style>
